// Rules & Terms tab content — renders admin-fed sections + formal safe disclaimer at bottom.
import React, { useCallback, useEffect, useState } from 'react';
import { ScrollText, Play } from 'lucide-react';
import api from '../../utils/api';
import Lightbox from '../../components/Lightbox';

// Render simple markdown-ish: **bold**, *italic*, line breaks, newlines to <br>.
function renderMd(txt = '') {
  return txt
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}

function ytEmbed(u = '') {
  const m = u.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function RulesTermsTab() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, images: [], idx: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/platform-content/rules_terms'); setSections(r.data.sections || []); }
    catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="mkt-rules-tab">
      <div className="flex items-center gap-2">
        <ScrollText size={20} className="text-amber-400" />
        <h2 className="text-lg font-black">Rules & Terms</h2>
      </div>

      {loading && <div className="text-center opacity-60 py-6 text-xs">⏳ Loading…</div>}

      {!loading && sections.length === 0 && (
        <div className="text-center opacity-60 py-8 text-xs bg-slate-900/40 rounded-xl border border-slate-800">
          Admin has not published any Rules & Terms sections yet
        </div>
      )}

      {sections.map((s) => (
        <div key={s.slug} className="rounded-2xl bg-slate-900/70 border border-slate-800 p-4 space-y-3" data-testid={`rules-section-${s.slug}`}>
          <h3 className="text-base font-black text-amber-200">{s.title}</h3>
          {s.body_md && <div className="text-sm opacity-90 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMd(s.body_md) }} />}
          {(s.images || []).length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {s.images.map((img, i) => (
                <img key={i} src={img} alt="" onClick={() => setLightbox({ open: true, images: s.images, idx: i })}
                     data-testid={`rules-img-${s.slug}-${i}`}
                     className="w-full aspect-square object-cover rounded-lg cursor-zoom-in hover:opacity-90 border border-slate-700" />
              ))}
            </div>
          )}
          {(s.video_urls || []).length > 0 && (
            <div className="space-y-2">
              {s.video_urls.map((v, i) => {
                const yt = ytEmbed(v);
                return yt ? (
                  <div key={i} className="aspect-video rounded-lg overflow-hidden bg-black">
                    <iframe src={yt} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={`video-${i}`} />
                  </div>
                ) : (
                  <video key={i} src={v} controls className="w-full rounded-lg" />
                );
              })}
            </div>
          )}
        </div>
      ))}

      {/* Mandatory legal-safe disclaimer — always shown */}
      <div className="rounded-2xl bg-amber-900/20 border-2 border-amber-400/40 p-4 text-xs leading-relaxed" data-testid="rules-disclaimer">
        <div className="font-black text-amber-300 mb-2 text-sm">⚠️ Formal Disclaimer</div>
        <p className="mb-2 opacity-90">
          Anant Marketplace is a technology facilitator connecting independent vendors, service providers,
          and buyers. All products, services, prices, and delivery timelines listed are the sole responsibility
          of the individual sellers. Anant Marketplace does not manufacture, warehouse, endorse, or guarantee
          any listed item.
        </p>
        <p className="mb-2 opacity-90">
          The AT (Anant Token) commission structure, referral cashback, and MLM cascade payouts are digital
          rewards issued from platform revenue for participation activities only. AT is <strong>not a currency,
          not a security, not an investment instrument</strong>, and carries no promise of monetary appreciation
          or fixed return. Users participate voluntarily and at their own risk.
        </p>
        <p className="mb-2 opacity-90">
          By using this platform, you agree that Anant Marketplace, its owners, and its affiliates shall not
          be liable for any direct, indirect, incidental, or consequential loss, damage, or dispute arising
          out of transactions between users. All disputes are subject to the jurisdiction of the local courts
          of the registered platform entity.
        </p>
        <p className="opacity-90">
          Users must comply with all applicable local laws including GST, consumer protection, and data-privacy
          regulations. Fraudulent activity, misrepresentation, or violation of these rules may lead to account
          suspension, forfeiture of pending AT, and legal action.
        </p>
        <div className="mt-3 pt-2 border-t border-amber-400/30 text-[10px] opacity-70">
          Last updated: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · Version 1.0
        </div>
      </div>

      {lightbox.open && (
        <Lightbox images={lightbox.images} startIndex={lightbox.idx} onClose={() => setLightbox({ open: false, images: [], idx: 0 })} />
      )}
    </div>
  );
}
